import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SectionHeader } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import {
  listApplications,
  listArchivedApplications,
} from '../../services/accApplicationsService';
import ACCApplicationCreateModal from './ACCApplicationCreateModal';
import ACCApplicationDetailModal from './ACCApplicationDetailModal';

const statusStyles = {
  pending: 'bg-amber-50 text-amber-800 border border-amber-200',
  accepted: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  denied: 'bg-rose-50 text-rose-800 border border-rose-200',
  archived: 'bg-slate-100 text-slate-500 border border-slate-200',
};

const statusLabels = {
  pending: 'Pending',
  accepted: 'Accepted',
  denied: 'Denied',
  archived: 'Archived',
};

const MaterialIcon = ({ name, size = 20, className = '' }) => (
  <span className={`material-symbols-outlined ${className}`} style={{ fontSize: `${size}px` }}>{name}</span>
);

const ArchitectureHeaderIcon = ({ className = '' }) => (
  <MaterialIcon name="architecture" className={className} size={20} />
);

const StatusPill = ({ status }) => (
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap ${statusStyles[status] || statusStyles.pending}`}>
    {statusLabels[status] || status}
  </span>
);

// 30-day CC&R response deadline. Only meaningful while the ACC hasn't touched the
// application yet (first_response_at set by DB trigger on first staff comment or
// decision) — once touched, the deadline is considered met and no badge shows.
const DeadlinePill = ({ application }) => {
  if (application.first_response_at || !application.response_due_at) return null;
  const dueDate = new Date(application.response_due_at);
  const daysLeft = Math.ceil((dueDate.getTime() - Date.now()) / 86400000);

  if (daysLeft < 0) {
    return (
      <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap bg-rose-50 text-rose-800 border border-rose-200">
        Overdue {Math.abs(daysLeft)}d
      </span>
    );
  }
  if (daysLeft <= 10) {
    return (
      <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap bg-amber-50 text-amber-800 border border-amber-200">
        Due in {daysLeft}d
      </span>
    );
  }
  return null;
};

const formatSubmitted = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays < 1) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 14) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const EmptyApplications = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30">
    <div className="w-16 h-16 bg-section-bg rounded-2xl flex items-center justify-center mb-6 warm-tactile-shadow">
      <MaterialIcon name="architecture" className="text-secondary" size={36} />
    </div>
    <h3 className="text-xl font-bold text-primary-dark mb-2">No active ACC applications</h3>
    <p className="text-sm font-medium text-slate-500 max-w-sm mb-8 leading-relaxed">
      Submitted exterior project requests will appear here for review, decision, and archival.
    </p>
  </div>
);

const ApplicationsTable = ({ applications, onOpen }) => (
  <div className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 overflow-hidden">
    <div className="hidden md:block">
      <table className="w-full">
        <thead className="bg-section-bg">
          <tr>
            <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Submitter</th>
            <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Project Title</th>
            <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Submitted</th>
            <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Status</th>
            <th className="text-left px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Comments</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-amber-100/30">
          {applications.map((application) => (
            <tr
              key={application.id}
              data-testid={`acc-row-${application.id}`}
              tabIndex={0}
              role="button"
              aria-label={`Open ACC application ${application.project_title}`}
              onClick={() => onOpen(application.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpen(application.id);
                }
              }}
              className="cursor-pointer hover:bg-secondary/10 focus:bg-secondary/10 focus:outline-none transition-colors"
            >
              <td className="px-6 py-5">
                <div className="text-sm font-bold text-primary-dark">{application.submitter_display_name}</div>
                <div className="text-xs font-medium text-slate-500 mt-1">{application.property_address_snapshot || 'No property linked'}</div>
              </td>
              <td className="px-6 py-5">
                <div className="text-sm font-bold text-primary-dark max-w-xs truncate">{application.project_title}</div>
              </td>
              <td className="px-6 py-5 text-sm font-medium text-slate-500">{formatSubmitted(application.created_at)}</td>
              <td className="px-6 py-5">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill status={application.status} />
                  <DeadlinePill application={application} />
                </div>
              </td>
              <td className="px-6 py-5">
                <span className={`inline-flex items-center justify-center min-w-9 h-9 rounded-full text-sm font-extrabold ${application.comment_count > 0 ? 'bg-secondary text-white' : 'bg-section-bg text-slate-500'}`}>
                  {application.comment_count}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="md:hidden divide-y divide-amber-100/40">
      {applications.map((application) => (
        <button
          key={application.id}
          data-testid={`acc-row-${application.id}`}
          type="button"
          aria-label={`Open ACC application ${application.project_title}`}
          onClick={() => onOpen(application.id)}
          className="w-full text-left p-5 space-y-4 hover:bg-section-bg/60 transition-colors"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-base font-bold text-primary-dark leading-snug break-words">{application.project_title}</p>
              <p className="text-sm font-medium text-slate-500 mt-1 break-words">
                {application.property_address_snapshot || 'No property linked'}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <StatusPill status={application.status} />
              <DeadlinePill application={application} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-section-bg border border-amber-100/40 p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Submitter</p>
              <p className="text-sm font-bold text-primary-dark mt-1 break-words">{application.submitter_display_name}</p>
            </div>
            <div className="rounded-2xl bg-section-bg border border-amber-100/40 p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Submitted</p>
              <p className="text-sm font-bold text-primary-dark mt-1">{formatSubmitted(application.created_at)}</p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 rounded-full bg-section-bg px-3 py-2 text-xs font-bold text-slate-500">
              <MaterialIcon name="chat_bubble" size={16} />
              {application.comment_count} comment{application.comment_count === 1 ? '' : 's'}
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-bold text-secondary">
              Open
              <MaterialIcon name="chevron_right" size={16} />
            </span>
          </div>
        </button>
      ))}
    </div>
  </div>
);

const ACCReview = () => {
  const { user } = useAuth();
  const { hoa } = useHOA();
  const [searchParams, setSearchParams] = useSearchParams();
  const hoaId = hoa?.id || user?.hoa_id;
  const currentUser = useMemo(() => ({
    id: user?.id,
    role: user?.portalRole || 'member',
    hoa_id: hoaId,
    is_acc_member: !!user?.is_acc_member,
  }), [hoaId, user?.id, user?.is_acc_member, user?.portalRole]);

  const [activeApplications, setActiveApplications] = useState([]);
  const [archivedApplications, setArchivedApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const selectedApplication = useMemo(() => (
    [...activeApplications, ...archivedApplications].find((application) => application.id === selectedApplicationId) || null
  ), [activeApplications, archivedApplications, selectedApplicationId]);

  const loadApplications = useCallback(async ({ showLoading = true, showErrors = true } = {}) => {
    if (!hoaId) {
      if (showLoading) setIsLoading(false);
      if (showErrors) setError('Community context is not ready. Please refresh or sign in again.');
      return false;
    }

    if (showLoading) setIsLoading(true);
    if (showErrors) setError('');

    try {
      const [activeResult, archivedResult] = await Promise.all([
        listApplications(hoaId, { status: ['pending', 'accepted', 'denied'] }),
        listArchivedApplications(hoaId),
      ]);

      if (activeResult.error || archivedResult.error) {
        const message = activeResult.error?.message || archivedResult.error?.message || 'Unable to load ACC applications.';
        if (showErrors) {
          setError(message);
        } else {
          console.warn('ACC background refresh failed:', message);
        }
        return false;
      } else {
        setActiveApplications(activeResult.data || []);
        setArchivedApplications(archivedResult.data || []);
        if (showErrors) setError('');
        return true;
      }
    } catch (loadError) {
      const message = loadError.message || 'Unable to load ACC applications.';
      if (showErrors) {
        setError(message);
      } else {
        console.warn('ACC background refresh failed:', message);
      }
      return false;
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [hoaId]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  useEffect(() => {
    const applicationId = searchParams.get('acc');
    if (applicationId) setSelectedApplicationId(applicationId);
  }, [searchParams]);

  const handleOpenDetail = useCallback((applicationId) => {
    if (!applicationId) return;
    setSelectedApplicationId(applicationId);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('acc', applicationId);
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleCloseDetail = () => {
    setSelectedApplicationId(null);
    if (!searchParams.has('acc')) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('acc');
    setSearchParams(nextParams, { replace: true });
  };

  const handleDeleted = useCallback((applicationId) => {
    setActiveApplications((current) => current.filter((application) => application.id !== applicationId));
    setArchivedApplications((current) => current.filter((application) => application.id !== applicationId));
    setSelectedApplicationId(null);
    setError('');

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('acc');
    setSearchParams(nextParams, { replace: true });

    loadApplications({ showLoading: false, showErrors: false }).then(null, (loadError) => {
      console.warn('ACC background refresh failed after delete:', loadError.message || loadError);
    });
  }, [loadApplications, searchParams, setSearchParams]);

  const handleCreated = useCallback((application) => {
    setError('');

    if (application?.id) {
      const optimisticApplication = {
        ...application,
        submitter_display_name:
          application.submitter_name
          || user?.full_name
          || user?.name
          || user?.email
          || 'Unknown submitter',
        decided_by_name: null,
        archived_by_name: null,
        comment_count: 0,
      };

      setActiveApplications((current) => (
        current.some((existing) => existing.id === optimisticApplication.id)
          ? current
          : [optimisticApplication, ...current]
      ));
    }

    setTimeout(() => {
      loadApplications({ showLoading: false, showErrors: false }).then(null, (loadError) => {
        console.warn('ACC background refresh failed:', loadError.message || loadError);
      });
    }, 1500);
  }, [loadApplications, user?.email, user?.full_name, user?.name]);

  return (
    <div className="space-y-6 text-primary-dark">
      <SectionHeader icon={ArchitectureHeaderIcon} title="ACC Applications" color="text-secondary" accent="bg-section-bg" />

      <section className="bg-section-bg p-8 rounded-[2.5rem] space-y-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Architectural Control Committee</p>
            <h2 className="text-3xl font-bold tracking-tight text-primary-dark">Application Workflow</h2>
          </div>
          <button
            type="button"
            data-testid="acc-new-application"
            onClick={() => setIsCreateOpen(true)}
            className="inline-flex items-center justify-center gap-2 bg-secondary text-white px-8 py-3.5 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all"
          >
            <MaterialIcon name="add_circle" size={20} />
            New Application
          </button>
        </div>

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-800">{error}</div>
        )}

        <div className="space-y-4" data-testid="acc-active-section">
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-xl font-bold text-primary-dark">Active Applications</h3>
            <span className="text-sm font-extrabold text-secondary">{activeApplications.length}</span>
          </div>

          {isLoading ? (
            <div className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 p-8 text-sm font-bold text-slate-500">Loading applications...</div>
          ) : activeApplications.length === 0 ? (
            <EmptyApplications />
          ) : (
            <ApplicationsTable applications={activeApplications} onOpen={handleOpenDetail} />
          )}
        </div>

        <details className="group bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 p-6" data-testid="acc-archived-section">
          <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-primary-dark">Archived Applications</h3>
              <p className="text-sm font-medium text-slate-500 mt-1">Read-only record of closed ACC applications.</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-extrabold text-secondary">{archivedApplications.length}</span>
              <MaterialIcon name="expand_more" className="text-primary-dark group-open:rotate-180 transition-transform" size={24} />
            </div>
          </summary>

          <div className="mt-6">
            {archivedApplications.length === 0 ? (
              <div className="bg-section-bg rounded-2xl p-6 text-sm font-bold text-slate-500">No archived applications yet.</div>
            ) : (
              <ApplicationsTable applications={archivedApplications} onOpen={handleOpenDetail} />
            )}
          </div>
        </details>
      </section>

      <ACCApplicationCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={handleCreated}
        currentUser={currentUser}
      />

      <ACCApplicationDetailModal
        isOpen={!!selectedApplicationId}
        applicationId={selectedApplicationId}
        initialApplication={selectedApplication}
        onClose={handleCloseDetail}
        onChanged={loadApplications}
        onDeleted={handleDeleted}
      />
    </div>
  );
};

export default ACCReview;
